'use client';

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SiteConductBlock } from '@/lib/cms-types';
import {
  CONDUCT_CATEGORIES,
  formatConductIndex,
  getConductItemPresentation,
  type ConductAppearance,
  type ConductCategoryId,
} from '@/lib/conduct-presentation';
import { getPowerLoreTheme } from '@/lib/power-lore';

/** Paleta azul da Lore (tema Normal) — só acentos no modo escuro. */
const lore = getPowerLoreTheme('normal');

const INTRO_TEXT =
  'Moderar com responsabilidade, respeito e bom senso protege quem busca ajuda e fortalece a confiança na equipe. Estas diretrizes orientam decisões justas no dia a dia do chat.';

type ConductCodesSectionProps = {
  conduct: SiteConductBlock;
  appearance?: ConductAppearance;
};

export default function ConductCodesSection({
  conduct,
  appearance = 'dark',
}: ConductCodesSectionProps) {
  const [filter, setFilter] = useState<ConductCategoryId>('all');
  const isLight = appearance === 'light';

  const items = useMemo(
    () =>
      conduct.items.map((item, index) => ({
        item,
        index,
        meta: getConductItemPresentation(item.bodyHtml, index),
      })),
    [conduct.items]
  );

  const visible = useMemo(
    () => (filter === 'all' ? items : items.filter((row) => row.meta.categoryId === filter)),
    [filter, items]
  );

  const categoryCounts = useMemo(() => {
    const counts: Partial<Record<ConductCategoryId, number>> = { all: items.length };
    for (const row of items) {
      counts[row.meta.categoryId] = (counts[row.meta.categoryId] ?? 0) + 1;
    }
    return counts;
  }, [items]);

  const navCategories = CONDUCT_CATEGORIES.filter(
    (c) => c.id === 'all' || (categoryCounts[c.id] ?? 0) > 0
  );

  const divider = isLight ? 'border-[rgba(0,11,106,0.14)]' : 'border-sky-500/20';

  const cardShell = cn(
    'relative overflow-hidden rounded-3xl border backdrop-blur-sm transition-colors duration-300',
    divider,
    isLight
      ? 'bg-white shadow-[0_12px_40px_-24px_rgba(0,11,106,0.2)]'
      : cn('bg-[#030023]/80', lore.cardShadow)
  );

  const richContentClass = cn(
    'font-inter text-[15px] leading-relaxed sm:text-base',
    '[&_p]:my-0',
    '[&_p+p]:mt-3',
    '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
    '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
    '[&_a]:underline-offset-2',
    isLight
      ? 'text-slate-600 [&_a]:text-[#000b6a] hover:[&_a]:text-[rgba(0,11,106,0.8)] [&_strong]:text-slate-900'
      : cn('text-blue-100/80', lore.richLink)
  );

  return (
    <div
      className={cn(
        'relative transition-colors duration-300',
        isLight ? 'bg-[#F4F7FC]' : 'bg-[#030023]'
      )}
    >
      {/* Introdução */}
      <section className="relative px-6 pb-10 pt-2 md:pb-14 md:pt-4">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={cn(cardShell, 'px-5 py-7 sm:px-8 sm:py-8')}
          >
            {!isLight ? (
              <>
                <div
                  className={cn(
                    'pointer-events-none absolute -left-20 top-10 h-56 w-56 rounded-full blur-3xl',
                    lore.orbPrimary
                  )}
                  aria-hidden
                />
                <div
                  className={cn(
                    'pointer-events-none absolute -right-16 bottom-0 h-48 w-48 rounded-full blur-3xl',
                    lore.orbSecondary
                  )}
                  aria-hidden
                />
              </>
            ) : (
              <div
                className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[rgba(0,11,106,0.06)] blur-3xl"
                aria-hidden
              />
            )}
            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-6">
              <span
                className={cn(
                  'inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border',
                  isLight
                    ? 'border-[rgba(0,11,106,0.16)] bg-gradient-to-b from-[rgba(0,11,106,0.08)] to-[rgba(0,11,106,0.02)] text-[#000b6a] shadow-[0_0_24px_-8px_rgba(0,11,106,0.3)]'
                    : 'border-sky-400/25 bg-gradient-to-b from-sky-400/15 to-transparent text-sky-200 shadow-[0_0_40px_-8px_rgba(56,189,248,0.55)]'
                )}
              >
                <ShieldCheck className="h-6 w-6" aria-hidden />
              </span>
              <div className="min-w-0">
                <p
                  className={cn(
                    'font-inter text-[10px] font-bold uppercase tracking-[0.18em]',
                    isLight ? 'text-[rgba(0,11,106,0.72)]' : lore.eyebrow
                  )}
                >
                  Introdução
                </p>
                <h2
                  className={cn(
                    'mt-1.5 font-poppins text-xl font-bold tracking-tight sm:text-2xl',
                    isLight ? 'text-slate-900' : 'text-white'
                  )}
                >
                  Mantendo nossa comunidade segura
                </h2>
                <p
                  className={cn(
                    'mt-3 max-w-2xl font-inter text-sm leading-relaxed sm:text-[15px]',
                    isLight ? 'text-slate-600' : 'text-blue-100/80'
                  )}
                >
                  {INTRO_TEXT}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Diretrizes */}
      <section className="relative px-6 pb-16 md:pb-24">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6 sm:mb-8"
          >
            <h2
              className={cn(
                'font-poppins text-2xl font-bold tracking-tight sm:text-3xl',
                isLight ? 'text-slate-900' : 'text-white'
              )}
            >
              Diretrizes de Conduta
            </h2>
            <p
              className={cn(
                'mt-2 max-w-2xl font-inter text-sm leading-relaxed sm:text-[15px]',
                isLight ? 'text-slate-600' : 'text-blue-100/75'
              )}
            >
              Documento oficial com as expectativas da equipe. Leia com atenção e aplique com
              critério.
            </p>
          </motion.div>

          {navCategories.length > 2 ? (
            <div className="-mx-1 mb-6 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mb-8">
              <div className="flex w-max gap-2" role="tablist" aria-label="Filtrar diretrizes">
                {navCategories.map((cat) => {
                  const active = filter === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setFilter(cat.id)}
                      className={cn(
                        'shrink-0 rounded-full border px-3.5 py-1.5 font-poppins text-xs font-semibold tracking-wide',
                        'transition-[color,background-color,border-color,box-shadow] duration-200',
                        active
                          ? isLight
                            ? 'border-[rgba(0,11,106,0.25)] bg-[#000b6a] text-white shadow-[0_8px_20px_-10px_rgba(0,11,106,0.45)]'
                            : 'border-sky-400/40 bg-sky-400/15 text-sky-50 shadow-[0_0_18px_-6px_rgba(56,189,248,0.55)]'
                          : isLight
                            ? 'border-[rgba(0,11,106,0.14)] bg-white text-slate-600 hover:border-[rgba(0,11,106,0.28)] hover:bg-[rgba(0,11,106,0.04)] hover:text-[#000b6a]'
                            : cn(
                                divider,
                                'bg-[#030023]/40 text-blue-100/75 hover:border-sky-400/35 hover:bg-sky-400/10 hover:text-sky-100'
                              )
                      )}
                    >
                      {cat.label}
                      <span
                        className={cn(
                          'ml-1.5 tabular-nums',
                          active
                            ? isLight
                              ? 'text-white/70'
                              : 'text-sky-200/90'
                            : isLight
                              ? 'text-slate-400'
                              : 'text-sky-400/60'
                        )}
                      >
                        {categoryCounts[cat.id] ?? 0}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className={cn(cardShell, 'p-0')}>
            {!isLight ? (
              <>
                <div
                  className={cn(
                    'pointer-events-none absolute -left-20 top-10 h-56 w-56 rounded-full blur-3xl',
                    lore.orbPrimary
                  )}
                  aria-hidden
                />
                <div
                  className={cn(
                    'pointer-events-none absolute -right-16 bottom-0 h-48 w-48 rounded-full blur-3xl',
                    lore.orbSecondary
                  )}
                  aria-hidden
                />
                <div
                  className={cn(
                    'pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent',
                    lore.topHairline
                  )}
                  aria-hidden
                />
              </>
            ) : (
              <div
                className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(0,11,106,0.22)] to-transparent"
                aria-hidden
              />
            )}

            <ol className="relative">
              {visible.map(({ item, index, meta }, listIndex) => {
                const Icon = meta.Icon;
                const isLast = listIndex === visible.length - 1;
                return (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-20px' }}
                    transition={{
                      duration: 0.35,
                      delay: Math.min(listIndex * 0.03, 0.24),
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className={cn(
                      'group relative px-4 py-6 sm:px-7 sm:py-7',
                      'transition-colors duration-200',
                      isLight ? 'hover:bg-[rgba(0,11,106,0.03)]' : 'hover:bg-sky-400/[0.06]',
                      !isLast && cn('border-b', divider)
                    )}
                  >
                    <span
                      className={cn(
                        'absolute bottom-0 left-0 top-0 w-0.5 transition-colors duration-200',
                        isLight
                          ? 'bg-[rgba(0,11,106,0.4)] group-hover:bg-[rgba(0,11,106,0.7)]'
                          : 'bg-sky-400/50 group-hover:bg-sky-400/80'
                      )}
                      aria-hidden
                    />

                    <div className="flex gap-4 sm:gap-5">
                      <div className="flex w-11 shrink-0 flex-col items-center gap-2 sm:w-14">
                        <span
                          className={cn(
                            'font-poppins text-2xl font-bold tabular-nums leading-none tracking-tight sm:text-3xl',
                            isLight
                              ? 'text-[rgba(0,11,106,0.82)] [text-shadow:0_0_10px_rgba(0,11,106,0.22),0_0_18px_rgba(0,11,106,0.12)]'
                              : 'text-sky-400/90 [text-shadow:0_0_12px_rgba(56,189,248,0.55),0_0_22px_rgba(56,189,248,0.35)]'
                          )}
                        >
                          {formatConductIndex(index)}
                        </span>
                        <span
                          className={cn(
                            'inline-flex h-7 w-7 items-center justify-center rounded-lg border transition-colors duration-200',
                            isLight
                              ? 'border-[rgba(0,11,106,0.14)] bg-[rgba(0,11,106,0.05)] text-[rgba(0,11,106,0.8)] group-hover:border-[rgba(0,11,106,0.28)] group-hover:bg-[rgba(0,11,106,0.08)] group-hover:text-[#000b6a]'
                              : 'border-sky-400/25 bg-sky-400/10 text-sky-300 group-hover:border-sky-400/40 group-hover:bg-sky-400/15 group-hover:text-sky-200'
                          )}
                        >
                          <Icon className="h-3.5 w-3.5" aria-hidden />
                        </span>
                      </div>

                      <div className="min-w-0 flex-1 pt-0.5">
                        <div className="mb-2.5 flex flex-wrap items-center gap-2">
                          <h3
                            className={cn(
                              'font-poppins text-[15px] font-semibold sm:text-base',
                              isLight ? 'text-slate-900' : 'text-white'
                            )}
                          >
                            {meta.title}
                          </h3>
                          <span
                            className={cn(
                              'rounded-md border px-2 py-0.5 font-poppins text-[10px] font-semibold uppercase tracking-[0.14em]',
                              isLight
                                ? 'border-[rgba(0,11,106,0.14)] bg-[rgba(0,11,106,0.06)] text-[#000b6a]'
                                : lore.badge
                            )}
                          >
                            {CONDUCT_CATEGORIES.find((c) => c.id === meta.categoryId)?.label}
                          </span>
                        </div>
                        <div
                          className={richContentClass}
                          dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
                        />
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </ol>

            {visible.length === 0 ? (
              <p
                className={cn(
                  'relative px-6 py-10 text-center font-inter text-sm',
                  isLight ? 'text-slate-500' : 'text-blue-100/70'
                )}
              >
                Nenhuma diretriz nesta categoria.
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
